import { observer } from 'mobx-react-lite';
import type { ApplicationSdk } from '../../../core/sdk';
import { useEffect } from 'react';
import { Button } from 'primereact/button';
import { InputSwitch } from 'primereact/inputswitch';
import { ObjectManagerStore, ObjectDialogStore } from '../';
import { formatDate } from '../../../utils/helper';
import { Dropdown } from 'primereact/dropdown';
import { MatenFilter, MatenTable, MatenTableAction, MatenTableColumn } from '../../../shared';

const ObjectOverview = observer(({ sdk }: { sdk: ApplicationSdk }) => {
    useEffect(() => {
        sdk.useStore(ObjectManagerStore);
    }, [sdk]);

    // Define columns for objects
    const columns: MatenTableColumn[] = [
        {
            field: 'object_number',
            header: 'Objekt-Id',
            sortable: true,
        },
        {
            field: 'name',
            header: 'Name',
            sortable: true,
        },
        {
            field: 'address',
            header: 'Adresse',
            sortable: false,
        },
        {
            field: 'manager.name',
            header: 'Objektleiter',
            sortable: false,
        },
        {
            field: 'created',
            header: 'Erstellt',
            sortable: true,
            body: (rowData: any) => formatDate(rowData.created),
        },
        {
            field: 'updated',
            header: 'Aktualisiert',
            sortable: true,
            body: (rowData: any) => formatDate(rowData.updated),
        },
    ];

    // Define actions for objects
    const actions: MatenTableAction[] = [
        {
            icon: 'pi pi-pencil',
            tooltip: 'Objekt bearbeiten',
            tooltipPosition: 'left',
            onClick: (rowData: any) => {
                sdk.openDialog(ObjectDialogStore, { object_id: rowData.id });
            },
            testId: 'edit-object-btn',
        },
        {
            icon: 'pi pi-trash',
            tooltip: 'Objekt löschen',
            tooltipPosition: 'left',
            onClick: async (rowData: any) => {
                const deleteResult = await sdk.getDialogDelete({
                    title: 'Objekt löschen',
                    message: `Möchten Sie das Objekt '${rowData.name}' wirklich löschen?`,
                    searchWord: 'löschen',
                    onConfirm: async () => sdk.currentStore.deleteObject(rowData.id),
                });

                if (deleteResult.confirmed) {
                    sdk.showSuccessToast('Objekt erfolgreich gelöscht');
                } else if (deleteResult.error) {
                    sdk.showErrorToast(deleteResult.error || 'Fehler beim Löschen des Objekts');
                }
            },
            onVisible: (rowData: any) => rowData.archived,
            testId: 'delete-object-btn',
            className: 'p-button-danger',
        },
    ];

    // Additional header content
    const headerContent = (
        <Button
            label={'Neu'}
            icon="pi pi-plus"
            aria-label="Filter"
            onClick={() => sdk.openDialog(ObjectDialogStore, {})}
            style={{ marginRight: '0.5rem' }}
            data-testid="add-object-btn"
        />
    );

    const tableFilter: MatenFilter[] = [
        {
            title: 'Objektleiter auswählen',
            property: 'filterManager',
            removeFilter: (store: any) => store.applyFilterManager(null),
            chipContent: (filter: any) => `Objektleiter: ${filter?.name || ''}`,
            filterComponent: (store: any) => (
                <Dropdown
                    value={store?.filterManager || null}
                    options={store?.available_managers || []}
                    onChange={(e: any) => store.applyFilterManager(e.value)}
                    onShow={() => store?.fetchAvailableManagers()} // Lazy load managers when dropdown is focused
                    placeholder="Objektleiter auswählen"
                    optionLabel="name"
                    loading={false}
                    style={{ width: '100%' }}
                    data-testid="manager-filter-dropdown"
                />
            ),
        },
        {
            title: 'Sonstige Filter',
            property: 'includeArchived',
            removeFilter: (store: any) => store.setIncludeArchived(false),
            chipContent: (filter: any) => `Archivierte Objekte`,
            filterComponent: (store: any) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <label htmlFor="showArchived">Archivierte Objekte</label>
                    <InputSwitch
                        id="showArchived"
                        checked={store?.includeArchived || false}
                        onChange={(e: any) => {
                            store.setIncludeArchived(e.value);
                        }}
                    />
                </div>
            ),
        },
    ];

    return (
        <MatenTable
            data={sdk.currentStore?.allObjects}
            store={sdk.currentStore}
            columns={columns}
            actions={actions}
            filter={tableFilter}
            search={{
                placeholder: 'Objekte durchsuchen',
            }}
            emptyMessage="Keine Objekte gefunden"
            entity={{ singular: 'Objekt', plural: 'Objekte' }}
            testId={'objects-overview'}
            toolbarContent={headerContent}
            pagination={{ rows: 30 }}
        />
    );
});

export default ObjectOverview;
